import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { Trophy, Users, Medal, Award, Maximize2, Minimize2, RefreshCw, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getActiveSportsYear } from '../../services/sportsYearService';

interface HousePerformance {
  house_id: string;
  house_name: string;
  house_color: string;
  competition_year: number;
  total_points: number;
  participating_students: number;
  total_participations: number;
  first_place_count: number;
  second_place_count: number;
  third_place_count: number;
}

interface Champion {
  champion_id: string;
  student_id: string;
  competition_year: number;
  gender: string;
  total_points: number;
  categories_won: number;
  student?: {
    first_name: string;
    last_name: string;
    first_name_tamil?: string;
    last_name_tamil?: string;
    house?: {
      house_id: string;
      house_name: string;
      house_color: string;
    };
  };
}

const HousePerformanceFullscreen: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const [housePerformance, setHousePerformance] = useState<HousePerformance[]>([]);
  const [champions, setChampions] = useState<Champion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // Add fullscreen change event listener
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // Clean up event listener
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    getActiveSportsYear()
      .then(setCurrentYear)
      .catch((err) => console.error('Error loading active sports year:', err));
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setRefreshing(true);
      
      // First try to get data from the house_performance view
      const { data: housePerformanceData, error: housePerformanceError } = await supabase
        .from('house_performance')
        .select('*')
        .eq('competition_year', currentYear)
        .order('total_points', { ascending: false });
      
      if (housePerformanceError) {
        console.error('Error fetching from house_performance view:', housePerformanceError);
      }
      
      // If we have house performance data from the view, use it
      if (housePerformanceData && housePerformanceData.length > 0) {
        setHousePerformance(housePerformanceData);
      } else {
        // Otherwise, calculate it manually
        // Get houses
        const { data: houses, error: housesError } = await supabase
          .from('houses')
          .select('*');
        
        if (housesError) {
          console.error('Error fetching houses:', housesError);
        }
        
        // Get all students with their house information
        const { data: students, error: studentsError } = await supabase
          .from('students')
          .select(`
            student_id,
            house_id,
            house:houses(
              house_id,
              house_name,
              house_color
            )
          `);
        
        if (studentsError) {
          console.error('Error fetching students:', studentsError);
        }
        
        // Get results for the current year
        const { data: results, error: resultsError } = await supabase
          .from('competition_results')
          .select(`
            result_id,
            student_id,
            position,
            points_earned
          `)
          .eq('competition_year', currentYear);
        
        if (resultsError) {
          console.error('Error fetching results:', resultsError);
        }
        
        if (houses && houses.length > 0 && students && students.length > 0) {
          // Create a map of student_id to house_id for quick lookup
          const studentHouseMap = new Map();
          students.forEach(student => {
            studentHouseMap.set(student.student_id, student.house_id);
          });
          
          // Calculate house performance manually
          const houseStats = houses.map(house => {
            // Get all results for students in this house
            const houseResults = results?.filter(r => {
              const studentHouseId = studentHouseMap.get(r.student_id);
              return studentHouseId === house.house_id;
            }) || [];
            
            // Count unique students who participated
            const participatingStudentIds = new Set(houseResults.map(r => r.student_id));
            
            // Count medals
            const firstPlace = houseResults.filter(r => r.position === 1).length;
            const secondPlace = houseResults.filter(r => r.position === 2).length;
            const thirdPlace = houseResults.filter(r => r.position === 3).length;
            
            // Calculate total points
            const totalPoints = houseResults.reduce((sum, r) => sum + r.points_earned, 0);
            
            return {
              house_id: house.house_id,
              house_name: house.house_name,
              house_color: house.house_color,
              competition_year: currentYear,
              total_points: totalPoints,
              participating_students: participatingStudentIds.size,
              total_participations: houseResults.length,
              first_place_count: firstPlace,
              second_place_count: secondPlace,
              third_place_count: thirdPlace
            };
          });
          
          // Sort by total points descending
          houseStats.sort((a, b) => b.total_points - a.total_points);
          setHousePerformance(houseStats);
        }
      }
      
      // Get champions for the current year
      const { data: championsData, error: championsError } = await supabase
        .from('champions')
        .select(`
          *,
          student:students(
            first_name,
            last_name,
            first_name_tamil,
            last_name_tamil,
            house:houses(
              house_id,
              house_name,
              house_color
            )
          )
        `)
        .eq('competition_year', currentYear)
        .order('total_points', { ascending: false });
      
      if (championsError) {
        console.error('Error fetching champions:', championsError);
      }
      
      setChampions(championsData || []);
      setLastUpdated(new Date());
      setError(null);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentYear]);

  useEffect(() => {
    fetchData();

    // Set up real-time subscription for competition results
    const resultsSubscription = supabase
      .channel('competition_results_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'competition_results' 
      }, () => {
        console.log('Results table changed, refreshing data...');
        fetchData();
      })
      .subscribe();

    // Set up real-time subscription for champions
    const championsSubscription = supabase
      .channel('champions_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'champions' 
      }, () => {
        console.log('Champions table changed, refreshing data...');
        fetchData();
      })
      .subscribe();

    // Set up refresh interval (every 30 seconds)
    const intervalId = setInterval(() => {
      fetchData();
    }, 30000);

    // Auto-enter fullscreen mode when component mounts
    const enterFullscreen = async () => {
      try {
        if (!document.fullscreenElement) {
          await document.documentElement.requestFullscreen();
        }
      } catch (err) {
        console.error('Error entering fullscreen:', err);
      }
    };
    
    enterFullscreen();

    return () => {
      resultsSubscription.unsubscribe();
      championsSubscription.unsubscribe();
      clearInterval(intervalId);
    };
  }, [currentYear, fetchData]);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
    }
  };

  // Calculate the maximum points to set the width of progress bars
  const maxPoints = Math.max(...housePerformance.map(house => house.total_points || 0), 1);

  // Group champions by house
  const championsByHouse = champions.reduce((acc, champion) => {
    if (champion.student?.house) {
      const houseId = champion.student.house.house_id;
      if (!acc[houseId]) {
        acc[houseId] = [];
      }
      acc[houseId].push(champion);
    }
    return acc;
  }, {} as Record<string, Champion[]>);

  // Function to change year for testing
  const changeYear = (year: number) => {
    setCurrentYear(year);
  };

  // Function to change language
  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  // Get translated text based on current language
  const getTranslatedText = (en: string, nn: string, ta: string) => {
    if (i18n.language === 'ta') return ta;
    if (i18n.language === 'nn') return nn;
    return en;
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-blue-900 to-indigo-900 text-white overflow-hidden">
      {/* Header with controls */}
      <header className="p-4 md:p-6 bg-black bg-opacity-30 backdrop-filter backdrop-blur-lg border-b border-white border-opacity-10 sticky top-0 z-10">
        <div className="max-w-full mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center">
            <Trophy className="h-8 w-8 md:h-10 md:w-10 text-yellow-400 mr-3 flex-shrink-0" />
            <h1 className="text-2xl md:text-4xl font-bold">
              {getTranslatedText(
                'House Performance', 
                'Hus-prestasjonar', 
                'இல்ல செயல்திறன்'
              )} {currentYear}
            </h1>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 md:gap-4">
            {/* Year selector */}
            <div className="flex space-x-1">
              <button 
                onClick={() => changeYear(2024)}
                className={`px-2 py-1 md:px-3 md:py-1 rounded text-sm md:text-base ${currentYear === 2024 ? 'bg-blue-600' : 'bg-blue-800 hover:bg-blue-700'} transition-colors`}
              >
                2024
              </button>
              <button 
                onClick={() => changeYear(2025)}
                className={`px-2 py-1 md:px-3 md:py-1 rounded text-sm md:text-base ${currentYear === 2025 ? 'bg-blue-600' : 'bg-blue-800 hover:bg-blue-700'} transition-colors`}
              >
                2025
              </button>
            </div>
            
            {/* Language selector */}
            <div className="flex space-x-1">
              <button 
                onClick={() => changeLanguage('en')}
                className={`px-2 py-1 md:px-3 md:py-1 rounded text-sm md:text-base ${i18n.language === 'en' ? 'bg-blue-600' : 'bg-blue-800 hover:bg-blue-700'} transition-colors`}
                aria-label="English"
              >
                EN
              </button>
              <button 
                onClick={() => changeLanguage('nn')}
                className={`px-2 py-1 md:px-3 md:py-1 rounded text-sm md:text-base ${i18n.language === 'nn' ? 'bg-blue-600' : 'bg-blue-800 hover:bg-blue-700'} transition-colors`}
                aria-label="Nynorsk"
              >
                NN
              </button>
              <button 
                onClick={() => changeLanguage('ta')}
                className={`px-2 py-1 md:px-3 md:py-1 rounded text-sm md:text-base ${i18n.language === 'ta' ? 'bg-blue-600' : 'bg-blue-800 hover:bg-blue-700'} transition-colors`}
                aria-label="Tamil"
              >
                TA
              </button>
            </div>
            
            {/* Action buttons */}
            <div className="flex space-x-2">
              <Link
                to="/"
                className="bg-blue-700 hover:bg-blue-600 text-white px-2 py-1 md:px-3 md:py-2 rounded-lg transition-colors flex items-center text-sm md:text-base"
              >
                <Home className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                <span className="hidden sm:inline">
                  {getTranslatedText('Home', 'Heim', 'முகப்பு')}
                </span>
              </Link>
              
              <button
                onClick={fetchData}
                className="bg-blue-700 hover:bg-blue-600 text-white px-2 py-1 md:px-3 md:py-2 rounded-lg transition-colors flex items-center text-sm md:text-base"
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">
                  {refreshing 
                    ? getTranslatedText('Refreshing...', 'Oppdaterer...', 'புதுப்பிக்கிறது...') 
                    : getTranslatedText('Refresh', 'Oppdater', 'புதுப்பி')}
                </span>
              </button>
              
              <button
                onClick={toggleFullscreen}
                className="bg-blue-700 hover:bg-blue-600 text-white px-2 py-1 md:px-3 md:py-2 rounded-lg transition-colors flex items-center text-sm md:text-base"
              >
                {isFullscreen ? (
                  <>
                    <Minimize2 className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                    <span className="hidden sm:inline">
                      {getTranslatedText('Exit', 'Avslutt', 'வெளியேறு')}
                    </span>
                  </>
                ) : (
                  <>
                    <Maximize2 className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                    <span className="hidden sm:inline">
                      {getTranslatedText('Fullscreen', 'Fullskjerm', 'முழுத்திரை')}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="p-4 md:p-6 max-w-full mx-auto">
        {error && (
          <div className="bg-red-800 text-white p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {loading && housePerformance.length === 0 ? (
          <div className="flex justify-center items-center h-[calc(100vh-200px)]">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
          </div>
        ) : housePerformance.length === 0 ? (
          <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg rounded-xl p-6 shadow-xl h-[calc(100vh-200px)] flex items-center justify-center">
            <p className="text-3xl text-center">
              {getTranslatedText(
                `No house performance data available for ${currentYear}`,
                `Ingen hus-prestasjonsdata tilgjengeleg for ${currentYear}`,
                `${currentYear} ஆண்டுக்கான இல்ல செயல்திறன் தரவு இல்லை`
              )}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:gap-8">
            {housePerformance.map((house, index) => (
              <div 
                key={house.house_id} 
                className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg rounded-xl p-4 md:p-6 shadow-xl transition-all duration-500 hover:bg-opacity-15"
                style={{
                  animation: `fadeIn 0.5s ease-out ${index * 0.2}s both`
                }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
                  <div className="flex items-center">
                    <div 
                      className="w-8 h-8 md:w-12 md:h-12 rounded-full mr-3 md:mr-4 flex-shrink-0" 
                      style={{ backgroundColor: house.house_color }}
                    />
                    <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold">{house.house_name}</h2>
                  </div>
                  <div className="text-3xl md:text-5xl lg:text-6xl font-bold">
                    {house.total_points} 
                    <span className="text-xl md:text-3xl ml-1">
                      {getTranslatedText('points', 'poeng', 'புள்ளிகள்')}
                    </span>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="relative h-8 md:h-12 bg-gray-700 bg-opacity-50 rounded-full overflow-hidden mb-6 md:mb-8">
                  <div 
                    className="absolute top-0 left-0 h-full rounded-full transition-all duration-1000 ease-out"
                    style={{ 
                      width: `${(house.total_points / maxPoints) * 100}%`,
                      backgroundColor: house.house_color,
                      opacity: 0.8
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center text-white text-lg md:text-2xl font-medium">
                    {Math.round((house.total_points / maxPoints) * 100)}%
                  </div>
                </div>
                
                {/* Stats grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6">
                  <div className="bg-black bg-opacity-20 rounded-lg p-4 md:p-6 flex items-center">
                    <Users className="h-8 w-8 md:h-12 md:w-12 text-blue-300 mr-3 md:mr-4 flex-shrink-0" />
                    <div>
                      <div className="text-sm md:text-lg text-gray-300">
                        {getTranslatedText('Students', 'Deltakarar', 'மாணவர்கள்')}
                      </div>
                      <div className="text-2xl md:text-4xl font-semibold">{house.participating_students}</div>
                    </div>
                  </div>
                  
                  <div className="bg-black bg-opacity-20 rounded-lg p-4 md:p-6 flex items-center">
                    <Medal className="h-8 w-8 md:h-12 md:w-12 text-yellow-400 mr-3 md:mr-4 flex-shrink-0" />
                    <div>
                      <div className="text-sm md:text-lg text-gray-300">
                        {getTranslatedText('1st Place', 'Førsteplass', 'முதல் இடம்')}
                      </div>
                      <div className="text-2xl md:text-4xl font-semibold">{house.first_place_count}</div>
                    </div>
                  </div>
                  
                  <div className="bg-black bg-opacity-20 rounded-lg p-4 md:p-6 flex items-center">
                    <Medal className="h-8 w-8 md:h-12 md:w-12 text-gray-300 mr-3 md:mr-4 flex-shrink-0" />
                    <div>
                      <div className="text-sm md:text-lg text-gray-300">
                        {getTranslatedText('2nd Place', 'Andreplass', 'இரண்டாம் இடம்')}
                      </div>
                      <div className="text-2xl md:text-4xl font-semibold">{house.second_place_count}</div>
                    </div>
                  </div>
                  
                  <div className="bg-black bg-opacity-20 rounded-lg p-4 md:p-6 flex items-center">
                    <Medal className="h-8 w-8 md:h-12 md:w-12 text-amber-700 mr-3 md:mr-4 flex-shrink-0" />
                    <div>
                      <div className="text-sm md:text-lg text-gray-300">
                        {getTranslatedText('3rd Place', 'Tredjeplass', 'மூன்றாம் இடம்')}
                      </div>
                      <div className="text-2xl md:text-4xl font-semibold">{house.third_place_count}</div>
                    </div>
                  </div>
                </div>

                {/* Champions for this house */}
                {championsByHouse[house.house_id] && championsByHouse[house.house_id].length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-xl md:text-2xl font-semibold mb-4 flex items-center">
                      <Award className="h-6 w-6 md:h-8 md:w-8 text-yellow-400 mr-3 flex-shrink-0" />
                      {getTranslatedText('Champions', 'Meistrar', 'சாம்பியன்கள்')}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                      {championsByHouse[house.house_id].map(champion => (
                        <div key={champion.champion_id} className="bg-white bg-opacity-10 rounded-lg p-3 md:p-4 flex items-center">
                          <div className="flex-shrink-0 bg-yellow-500 bg-opacity-20 p-2 md:p-3 rounded-full mr-3 md:mr-4">
                            <Trophy className="h-6 w-6 md:h-8 md:w-8 text-yellow-400" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-base md:text-xl truncate">
                              {champion.student?.first_name_tamil && champion.student?.last_name_tamil && i18n.language === 'ta'
                                ? `${champion.student.first_name_tamil} ${champion.student.last_name_tamil}`
                                : `${champion.student?.first_name} ${champion.student?.last_name}`}
                            </div>
                            <div className="text-sm md:text-base text-gray-300 truncate">
                              {champion.categories_won} {getTranslatedText('categories', 'kategoriar', 'பிரிவுகள்')} | {champion.total_points} {getTranslatedText('points', 'poeng', 'புள்ளிகள்')}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
      
      <footer className="p-4 text-center text-gray-300 text-sm md:text-base">
        <p>
          {getTranslatedText(
            'Last updated: ', 
            'Sist oppdatert: ', 
            'கடைசியாக புதுப்பிக்கப்பட்டது: '
          )}{lastUpdated.toLocaleTimeString()}
        </p>
      </footer>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default HousePerformanceFullscreen;